import classNames from 'classnames'
import { ButtonProps, ButtonSize, SharedProps } from '@/types/Button.types'
import styles from '@/app/_components/Button.module.css'

/**
 * Get the size class for the button
 * Default to Md and base size if no size is provided
 * @param {ButtonSize} size
 */
const getSizeClass = (size?: ButtonSize): string[] => {
  switch (size) {
  case 'sm':
    return [styles.Sm, 'mw--text-sm']
  case 'xs':
    return [styles.Xs, 'mw--text-xs']
  case 'lg':
    return [styles.Lg, 'mw--text-lg']
  case 'xl':
    return [styles.Lg, 'mw--text-xl']
  case '2xl':
    return [styles.Lg, 'mw--text-2xl']
  case '3xl':
    return [styles.Lg, 'mw--text-3xl']
  case 'md':
  case undefined:
  default:
    return [styles.Md, 'mw--text-base']
  }
}

/**
 * Shared props for buttons and anchor buttons
 * Props added to the button or anchor tags
 * @param {Props} props
 * @returns {SharedProps}
 */
export const getSharedProps = ({
  className,
  color = 'primary',
  disabled,
  icon,
  size,
  testId,
  uppercase = false,
  variant = 'default',
  weight,
  title,
  href,
  onClick,
  type,
  rel,
  ...rest // capture any additional props
}: ButtonProps): SharedProps => ({
  className: classNames(
    'mw--transition-color',
    className,
    styles.Button,
    'mw--button', // referenced in css
    getSizeClass(size),
    {
      [styles.Disabled]: disabled,
      [styles.WithIcon]: !!icon,
      'with-icon': !!icon,
      [styles.Uppercase]: uppercase,
      // color
      [styles.Secondary]: color === 'secondary',
      [styles.Primary]: color !== 'secondary',
      // variant
      [styles.Inverse]: variant === 'inverse',
      [styles.Outline]: variant === 'outline',
      // font weight
      'mw--font-bold': weight === 'bold',
      'mw--font-semibold': weight === 'semibold',
      'mw--font-medium': weight === 'medium',
      'mw--font-normal': weight === 'normal',
    },
  ),
  disabled,
  ...(testId ? { 'data-testid': testId } : {}),
  title,
  href,
  onClick,
  rel,
  type,
  ...rest,
})
